import React from "react";
import { Box } from "@mui/joy";
import {
  FormatBoldIcon as FormatBold,
  FormatItalicIcon as FormatItalic,
  LinkIcon,
  SmartButtonOutlinedIcon as SmartButtonOutlined,
} from "../../../../styles/StyledMUI";
import IconButtonCTA from "../../../common/IconButtonCTA";
import {
  DEFAULT_MAIL_EDITOR_LINK_HREF,
  escapeHtmlAttr,
  mailCtaButtonLinkOpenClose,
} from "../mailLineParts";
import { wrapHtmlSelection } from "../mailEditorHtmlWrap";
import MailEditorLinkUrlModal from "./MailEditorLinkUrlModal";
import CuratedEmojiPicker from "../../../common/CuratedEmojiPicker";
import { insertSnippetInTextField } from "../mailEditorShared";

const LINK_CLOSE = "</a>";

function hrefAndMidFromSelectionOrDefault(sel: string): { href: string; mid: string } {
  const m = sel.match(/^<a\s[^>]*\bhref="([^"]*)"[^>]*>([\s\S]*)<\/a>$/i);

  if (m) {
    return { href: m[1], mid: m[2].length > 0 ? m[2] : "" };
  }

  return {
    href: DEFAULT_MAIL_EDITOR_LINK_HREF,
    mid: sel.length > 0 ? sel : "Action",
  };
}

type MailFormatToolbarProps = {
  canEdit: boolean;
  value: string;
  onValueChange: (next: string) => void;
  textareaRef: React.RefObject<HTMLTextAreaElement | null>;
};

export default function MailFormatToolbar(props: MailFormatToolbarProps) {
  const { canEdit, value, onValueChange, textareaRef } = props;
  const [linkUrlModalOpen, setLinkUrlModalOpen] = React.useState(false);

  const applyWrap = React.useCallback(
    (open: string, close: string, emptyFallback: string) => {
      const ta = textareaRef.current;

      if (!ta || !canEdit) return;

      const { next, selStart, selEnd } = wrapHtmlSelection(
        value,
        ta.selectionStart,
        ta.selectionEnd,
        open,
        close,
        emptyFallback,
      );

      onValueChange(next);

      requestAnimationFrame(() => {
        ta.focus();
        ta.setSelectionRange(selStart, selEnd);
      });
    },
    [canEdit, onValueChange, textareaRef, value],
  );

  const applyButtonLinkWrap = React.useCallback(() => {
    const ta = textareaRef.current;

    if (!ta || !canEdit) return;

    const start = ta.selectionStart;
    const end = ta.selectionEnd;
    const sel = value.slice(start, end);
    const { href, mid } = hrefAndMidFromSelectionOrDefault(sel);
    const { open, close } = mailCtaButtonLinkOpenClose(href);
    const next = value.slice(0, start) + open + mid + close + value.slice(end);
    const selStart = start + open.length;
    const selEnd = selStart + mid.length;

    onValueChange(next);

    requestAnimationFrame(() => {
      ta.focus();
      ta.setSelectionRange(selStart, selEnd);
    });
  }, [canEdit, onValueChange, textareaRef, value]);

  const linkSelectionRef = React.useRef<{ start: number; end: number } | null>(null);

  const openLinkUrlModalForWrap = React.useCallback(() => {
    const ta = textareaRef.current;

    if (!ta || !canEdit) {
      return;
    }

    linkSelectionRef.current = {
      start: ta.selectionStart,
      end: ta.selectionEnd,
    };
    setLinkUrlModalOpen(true);
  }, [canEdit, textareaRef]);

  const confirmLinkWrap = React.useCallback(
    (entered: string) => {
      const ta = textareaRef.current;
      const sel = linkSelectionRef.current;

      if (!ta || !sel) {
        return;
      }

      const href = entered.trim() || DEFAULT_MAIL_EDITOR_LINK_HREF;
      const open = `<a href="${escapeHtmlAttr(href)}" target="_blank" rel="noopener noreferrer">`;
      const { next, selStart, selEnd } = wrapHtmlSelection(
        value,
        sel.start,
        sel.end,
        open,
        LINK_CLOSE,
        "",
      );

      onValueChange(next);

      requestAnimationFrame(() => {
        ta.focus();
        ta.setSelectionRange(selStart, selEnd);
      });

      linkSelectionRef.current = null;
    },
    [onValueChange, textareaRef, value],
  );

  return (
    <>
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mb: 0.5, alignItems: "center" }}>
        <IconButtonCTA
          aria-label="Mettre la sélection en gras"
          size="sm"
          ctaVariant="accent"
          disabled={!canEdit}
          onClick={() => applyWrap("<b>", "</b>", "")}
        >
          <FormatBold fontSize="small" />
        </IconButtonCTA>
        <IconButtonCTA
          aria-label="Mettre la sélection en italique"
          size="sm"
          ctaVariant="accent"
          disabled={!canEdit}
          onClick={() => applyWrap("<i>", "</i>", "")}
        >
          <FormatItalic fontSize="small" />
        </IconButtonCTA>
        <IconButtonCTA
          aria-label="Entourer la sélection d’un lien (choisir l’URL)"
          size="sm"
          ctaVariant="accent"
          disabled={!canEdit}
          onClick={() => openLinkUrlModalForWrap()}
        >
          <LinkIcon fontSize="small" />
        </IconButtonCTA>
        <IconButtonCTA
          aria-label="Style bouton jaune : même URL que l’icône lien par défaut, ou sélectionner un lien existant pour garder son adresse (ex. lien d’invitation)"
          size="sm"
          ctaVariant="accent"
          disabled={!canEdit}
          onClick={() => applyButtonLinkWrap()}
        >
          <SmartButtonOutlined fontSize="small" />
        </IconButtonCTA>
        {canEdit ? (
          <CuratedEmojiPicker
            menuZIndex={2500}
            triggerSizePx={32}
            onPick={(emoji) => {
              insertSnippetInTextField(textareaRef.current, value, emoji, onValueChange);
            }}
          />
        ) : null}
      </Box>
      <MailEditorLinkUrlModal
        open={linkUrlModalOpen}
        onClose={() => {
          setLinkUrlModalOpen(false);
          linkSelectionRef.current = null;
        }}
        onConfirm={confirmLinkWrap}
        title="URL du lien"
      />
    </>
  );
}
